// SSO Callback Handler Page
// PBI 760: Handle SSO callback redirection and URL cleaning

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSSO } from '../../hooks/useSSO';
import { useAuth } from '../../contexts/AuthContext';

const SSOCallback: React.FC = () => {
  const navigate = useNavigate();
  const { handleSSOCallback } = useSSO();
  const { checkSession } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your sign-in...');

  useEffect(() => {
    let pollingInterval: NodeJS.Timeout;
    let attempts = 0;
    const maxAttempts = 10; // Increased for Google SSO timing

    const pollSession = async () => {
      const isAuthenticated = await checkSession();
      if (isAuthenticated) {
        setStatus('success');
        setMessage('Sign-in successful! Redirecting...');
        
        const returnUrl = localStorage.getItem('sso_return_url') || '/';
        localStorage.removeItem('sso_return_url');
        
        setTimeout(() => {
          navigate(returnUrl);
        }, 500);
        clearInterval(pollingInterval);
      } else if (++attempts >= maxAttempts) {
        setStatus('error');
        setMessage('Sign-in failed. Please try again.');
        clearInterval(pollingInterval);
      }
    };

    const processCallback = async () => {
      try {
        const result = await handleSSOCallback();
        if (result.success) {
          // Less aggressive polling - wait longer between checks
          pollingInterval = setInterval(pollSession, 1000);
        } else if (result.hasValidToken && result.pendingAuth) {
          // We have valid credentials but session not ready yet - continue polling
          setMessage('Completing sign-in...');
          pollingInterval = setInterval(pollSession, 1000);
        } else {
          setStatus('error');
          setMessage(result.error || 'Sign-in failed. Please try again.');
          setTimeout(() => {
            navigate('/auth/login?error=' + encodeURIComponent(result.error || 'Unknown error'));
          }, 3000);
        }
      } catch (error) {
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
        setTimeout(() => {
          navigate('/auth/login?error=callback_error');
        }, 3000);
      }
    };

    processCallback();
    return () => clearInterval(pollingInterval);
  }, [handleSSOCallback, navigate, checkSession]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-sm text-center">
        <div className="mb-6">
          {status === 'processing' && (
            <div className="flex justify-center mb-4">
              <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          
          {status === 'success' && (
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
          )}
        </div>

        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          {status === 'processing' && 'Completing Sign-In'}
          {status === 'success' && 'Welcome to Prompt Shelf!'}
          {status === 'error' && 'Sign-In Failed'}
        </h1>

        <p className="text-gray-600 mb-6">
          {message}
        </p>

        {status === 'error' && (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/auth/login')}
              className="w-full bg-brand-dark-blue text-white py-2 px-4 rounded-md hover:bg-brand-electric-blue transition-colors"
            >
              Try Again
            </button>
                        </div>
          )}
      </div>
    </div>
  );
};

export default SSOCallback;